/**
 * Translation Helper Utilities
 * 
 * Provides utilities for safe translation handling with fallbacks
 * and validation for untranslated keys.
 */

import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * Checks if a string appears to be an untranslated translation key
 */
export function isUntranslatedKey(text: string): boolean {
  // Check for patterns like "notifications.autoTrading.enabledTitle"
  return (
    typeof text === 'string' &&
    text.includes('.') &&
    !text.includes(' ') &&
    text.length > 5 &&
    /^[a-zA-Z][a-zA-Z0-9.]*[a-zA-Z0-9]$/.test(text)
  );
}

/**
 * Extracts a human-readable fallback from a translation key
 */
export function extractFallbackFromKey(key: string): string {
  if (!isUntranslatedKey(key)) {
    return key;
  }
  
  // Extract the last part of the key and make it human-readable
  const lastPart = key.split('.').pop() || key;
  
  // Convert camelCase to Title Case
  return lastPart
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Safe translation function that provides fallbacks for missing keys
 */
export function safeTranslate(
  t: (key: string, options?: any) => string,
  key: string,
  options?: any,
  fallback?: string
): string {
  try {
    const translated = t(key, options);
    
    // If the translation returns the key itself, it's likely missing
    if (translated === key || isUntranslatedKey(translated)) {
      console.warn(`Translation missing for key: ${key}`);
      return fallback || extractFallbackFromKey(key);
    }
    
    return translated;
  } catch (error) {
    console.error(`Translation error for key ${key}:`, error);
    return fallback || extractFallbackFromKey(key);
  }
}

/**
 * Enhanced useTranslation hook with safe translation
 */
export function useSafeTranslation() {
  const { t: originalT, ...rest } = useI18nTranslation();
  
  const t = (key: string, options?: any, fallback?: string) => {
    return safeTranslate(originalT, key, options, fallback);
  };
  
  return { t, ...rest };
}

/**
 * Toast-specific translation helper
 */
export interface ToastTranslationProps {
  title?: string;
  description?: string;
  titleFallback?: string;
  descriptionFallback?: string;
}

export function translateToastProps(
  t: (key: string, options?: any) => string,
  props: ToastTranslationProps
): { title?: string; description?: string } {
  const result: { title?: string; description?: string } = {};
  
  if (props.title) {
    result.title = safeTranslate(t, props.title, undefined, props.titleFallback);
  }
  
  if (props.description) {
    result.description = safeTranslate(t, props.description, undefined, props.descriptionFallback);
  }
  
  return result;
}

/**
 * Validates translation content and provides fallbacks
 */
export function validateTranslationContent(content?: React.ReactNode): React.ReactNode {
  if (typeof content === 'string' && isUntranslatedKey(content)) {
    console.warn(`Untranslated key detected: ${content}`);
    return extractFallbackFromKey(content);
  }
  return content;
}

/**
 * Date formatting utilities with proper localization and GMT timezone
 */
export interface DateFormatOptions {
  timeZone?: string;
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  weekday?: 'long' | 'short' | 'narrow';
  year?: 'numeric' | '2-digit';
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
}

/**
 * Formats a date with proper localization while maintaining GMT timezone
 */
export function formatLocalizedDate(
  date: string | Date,
  locale: string,
  options: DateFormatOptions = {}
): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Default to GMT timezone if not specified
    const formatOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'GMT',
      ...options
    };

    const formatted = new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);

    // Append GMT if timezone is GMT and not already included
    if (formatOptions.timeZone === 'GMT' && (options.hour || options.minute || options.timeStyle)) {
      return `${formatted} GMT`;
    }

    return formatted;
  } catch (error) {
    console.error('Date formatting error:', error);
    // Fallback to basic formatting
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
  }
}

/**
 * Formats a date for display in the current user's locale with GMT timezone
 */
export function formatGMTDate(date: string | Date, locale: string = 'en-US'): string {
  return formatLocalizedDate(date, locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'GMT'
  });
}

/**
 * Formats a datetime for display in the current user's locale with GMT timezone
 */
export function formatGMTDateTime(date: string | Date, locale: string = 'en-US'): string {
  return formatLocalizedDate(date, locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'GMT'
  });
}

/**
 * Formats a short datetime for display in the current user's locale with GMT timezone
 */
export function formatGMTDateTimeShort(date: string | Date, locale: string = 'en-US'): string {
  return formatLocalizedDate(date, locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'GMT'
  });
}

/**
 * Formats a simple date for display in the current user's locale
 */
export function formatSimpleDate(date: string | Date, locale: string = 'en-US'): string {
  return formatLocalizedDate(date, locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });
}

/**
 * Formats a payment deadline with forced 00:00 GMT time for consistency
 */
export function formatPaymentDeadline(date: string | Date, locale: string = 'en-US'): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Create a new date object set to Saturday 00:00 GMT
    const saturdayMidnight = new Date(dateObj);
    saturdayMidnight.setUTCHours(0, 0, 0, 0);

    // Find the next Saturday if not already Saturday
    const dayOfWeek = saturdayMidnight.getUTCDay();
    if (dayOfWeek !== 6) { // 6 = Saturday
      const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
      saturdayMidnight.setUTCDate(saturdayMidnight.getUTCDate() + daysUntilSaturday);
    }

    return formatLocalizedDate(saturdayMidnight, locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'GMT'
    });
  } catch (error) {
    console.error('Payment deadline formatting error:', error);
    // Fallback to regular GMT datetime formatting
    return formatGMTDateTime(date, locale);
  }
}

/**
 * Formats a short payment deadline with forced 00:00 GMT time
 */
export function formatPaymentDeadlineShort(date: string | Date, locale: string = 'en-US'): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Create a new date object set to Saturday 00:00 GMT
    const saturdayMidnight = new Date(dateObj);
    saturdayMidnight.setUTCHours(0, 0, 0, 0);

    // Find the next Saturday if not already Saturday
    const dayOfWeek = saturdayMidnight.getUTCDay();
    if (dayOfWeek !== 6) { // 6 = Saturday
      const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
      saturdayMidnight.setUTCDate(saturdayMidnight.getUTCDate() + daysUntilSaturday);
    }

    return formatLocalizedDate(saturdayMidnight, locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'GMT'
    });
  } catch (error) {
    console.error('Payment deadline short formatting error:', error);
    // Fallback to regular GMT datetime formatting
    return formatGMTDateTimeShort(date, locale);
  }
}
